import {
  type CustomerDto,
  InformationCardConfig,
  type SimplifiedCustomerDto,
  keyValueFieldsToObject,
} from 'korint-sdk';

export const ContactKey = {
  FIRST_NAME: 'FIRST_NAME',
  LAST_NAME: 'LAST_NAME',
  COMPANY_NAME: 'COMPANY_NAME',
  TEMPORARY_CUSTOMER_NAME: 'TEMPORARY_CUSTOMER_NAME',
} as const satisfies Record<string, string>;

const extractFieldValues = (
  fieldsConfig: InformationCardConfig['fieldsConfig'],
  customerCustomFields: Record<string, unknown>,
  contactKeys: string | string[],
): string | null => {
  const keysArray = Array.isArray(contactKeys) ? contactKeys : [contactKeys];

  const matchingFields = fieldsConfig
    .filter(
      shortcut =>
        typeof shortcut === 'object' &&
        'contactKey' in shortcut &&
        keysArray.includes(shortcut.contactKey ?? ''),
    )
    .map(shortcut =>
      typeof shortcut === 'object' && 'key' in shortcut
        ? customerCustomFields[shortcut.key]
        : undefined,
    )
    .filter(value => value);

  return matchingFields.length > 0 ? matchingFields.join(' ') : null;
};

export const customerDisplayName = (
  customers: CustomerDto[] | SimplifiedCustomerDto[],
  fieldsConfig: InformationCardConfig['fieldsConfig'] | undefined,
  role?: string,
): string | null => {
  const customer =
    (role ? customers.find(c => 'role' in c && c.role === role) : customers[0]) ?? customers[0];
  const customerCustomFields = keyValueFieldsToObject(customer?.customFields ?? []);

  if (!fieldsConfig) {
    return null;
  }

  const companyName = extractFieldValues(
    fieldsConfig,
    customerCustomFields,
    ContactKey.COMPANY_NAME,
  );
  if (companyName) {
    return companyName;
  }

  const fullName = extractFieldValues(fieldsConfig, customerCustomFields, [
    ContactKey.FIRST_NAME,
    ContactKey.LAST_NAME,
  ]);
  if (fullName) {
    return fullName;
  }

  const temporaryName = extractFieldValues(
    fieldsConfig,
    customerCustomFields,
    ContactKey.TEMPORARY_CUSTOMER_NAME,
  );
  if (temporaryName) {
    return temporaryName;
  }

  return null;
};
